import { type PartialDeep } from 'type-fest';

import { type CanvasElementType, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import {
  type CanvasElementDirtyFieldProps,
  type CanvasElementWithDirtyFieldProps,
  type ElementLabelPropsWithAdvancedAlign,
  type ElementLabelPropsWithPositionAndWidth,
} from '@blaze-shared/elements-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import {
  type ParentChildrenContentOverrides,
  type ParentChildrenStyleOverrides,
} from '../../canvas-elements/parent-overrides';

export const getClearElementContentOverridesPropsPatch = (): {
  content: {
    title: PartialDeep<ElementLabelPropsWithAdvancedAlign>;
  };
  dirty: Partial<CanvasElementDirtyFieldProps>;
} => ({
  content: {
    title: {
      position: undefined,
      align: { horizontal: undefined },
      width: undefined,
    },
  },
  dirty: {
    width: false,
    height: false,
    labelWidth: false,
  },
});

export const getClearElementContentOverridesPropsPatchWithSimpleAlign = (): {
  content: {
    title: PartialDeep<ElementLabelPropsWithPositionAndWidth>;
  };
  dirty: Partial<CanvasElementDirtyFieldProps>;
} => ({
  content: {
    title: {
      position: undefined,
      align: undefined,
      width: undefined,
    },
  },
  dirty: {
    width: false,
    height: false,
    labelWidth: false,
  },
});

export type ElementLabelWidthProps = {
  content?: { title: ElementLabelPropsWithPositionAndWidth | ElementLabelPropsWithAdvancedAlign };
} & CanvasElementWithDirtyFieldProps;
export const getLabelWidthOverride = (
  overrides: ParentChildrenContentOverrides,
  elementProps: ElementLabelWidthProps,
): undefined | { content: { title: { width: string | undefined } } } => {
  const titleWidth = elementProps.content?.title?.width;
  const titleWidthDirty = elementProps.dirty?.labelWidth ?? false;
  const noPropsValue = !titleWidth || !titleWidthDirty;
  if (noPropsValue) {
    const width = overrides.label?.widthMode === 'label' ? 'auto' : overrides.label?.width;
    if (width) {
      return { content: { title: { width } } };
    }
  }
  return undefined;
};

export function shouldPatchProp<T extends CanvasElementType>(
  overrideValue: any,
  path: string,
  elementProps: PartialPropsOf<T> | undefined,
  initialValues: PartialPropsOf<T> | undefined,
): overrideValue is true {
  const propsValue = getValueByPath(elementProps, path);
  return (
    overrideValue && (!propsValue || isDeepEqual(propsValue, getValueByPath(initialValues, path)))
  );
}

/** Shared form → field label layout (position, align, width). */
export function applySimpleAlignParentContentOverrides<T extends CanvasElementType>(
  patch: (partial: PartialPropsOf<T>) => void,
  elementProps: PartialPropsOf<T>,
  overrides: ParentChildrenContentOverrides | null,
  initialValues?: PartialPropsOf<T>,
): void {
  if (overrides === null) {
    patch(getClearElementContentOverridesPropsPatchWithSimpleAlign() as PartialPropsOf<T>);
    return;
  }

  if (
    shouldPatchProp(
      overrides.label?.position,
      'content.title.position',
      elementProps,
      initialValues,
    )
  ) {
    patch({ content: { title: { position: overrides.label.position } } } as PartialPropsOf<T>);
  }

  if (shouldPatchProp(overrides.label?.align, 'content.title.align', elementProps, initialValues)) {
    patch({ content: { title: { align: overrides.label.align } } } as PartialPropsOf<T>);
  }

  const titlePatch = getLabelWidthOverride(overrides, elementProps as ElementLabelWidthProps);
  if (titlePatch) {
    patch(titlePatch as PartialPropsOf<T>);
  }
}

export type ParentStyleOverrideRule<T extends CanvasElementType> = {
  value: unknown;
  path: string;
  patch: PartialPropsOf<T>;
};

/** Applies explicit form → element style mappings when overrides are not user-customized. */
export function applyParentStyleOverrideRules<T extends CanvasElementType>(
  patch: (partial: PartialPropsOf<T>) => void,
  elementProps: PartialPropsOf<T>,
  overrides: ParentChildrenStyleOverrides | null,
  initialValues: PartialPropsOf<T> | undefined,
  clearPatch: PartialPropsOf<T>,
  rules: ParentStyleOverrideRule<T>[],
): void {
  if (overrides === null) {
    patch(clearPatch);
    return;
  }

  for (const rule of rules) {
    if (shouldPatchProp(rule.value, rule.path, elementProps, initialValues)) {
      patch(rule.patch);
    }
  }
}
